import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { Incoterm } from '../types/incoterm';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const IncotermManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingIncoterm, setEditingIncoterm] = useState<Incoterm | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch incoterms with server-side filtering and pagination
  const {
    data: incotermsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'incoterms',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/incoterm?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('Incoterm API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const incoterms = incotermsResponse?.data || [];
  const pagination = incotermsResponse?.pagination;

  // Create mutation
  const createIncotermMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/incoterm', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incoterms'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'incoterm'],
      });
      toast({
        title: 'Success',
        description: 'Incoterm created successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create incoterm';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateIncotermMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(
        `/api/master-data/incoterm/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incoterms'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'incoterm'],
      });
      toast({
        title: 'Success',
        description: 'Incoterm updated successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setEditingIncoterm(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update incoterm';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation (hard delete)
  const deleteIncotermMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/incoterm/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incoterms'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'incoterm'],
      });
      toast({
        title: 'Success',
        description: 'Incoterm deleted successfully',
        variant: 'default',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete incoterm',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration
  const columns: TableColumn<Incoterm>[] = [
    {
      key: 'srNo',
      label: 'SR No.',
      sortable: true,
      render: (_, incoterm) => (
        <div className="font-mono text-sm">{incoterm.srNo}</div>
      ),
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (_, incoterm) => (
        <div className="font-medium">{incoterm.name}</div>
      ),
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (_, incoterm) => (
        <div className="font-mono text-sm bg-muted px-2 py-1 rounded">
          {incoterm.code}
        </div>
      ),
    },
    {
      key: 'hideInPortal',
      label: 'Portal Visibility',
      sortable: true,
      render: (_, incoterm) => (
        <Badge
          variant={incoterm.hideInPortal ? 'secondary' : 'outline'}
          className={!incoterm.hideInPortal ? 'border-blue-500 text-blue-600' : ''}
        >
          {incoterm.hideInPortal ? 'Hidden' : 'Visible'}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, incoterm) => (
        <Badge
          variant={incoterm.status === 'Active' ? 'default' : 'secondary'}
          className={
            incoterm.status === 'Active'
              ? 'bg-green-500 hover:bg-green-600'
              : ''
          }
        >
          {incoterm.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (_, incoterm) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(incoterm.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      placeholder: 'Filter by status',
    },
    {
      key: 'hideInPortal',
      label: 'Portal Visibility',
      type: 'select',
      options: [
        { value: 'all', label: 'All' },
        { value: 'true', label: 'Hidden' },
        { value: 'false', label: 'Visible' },
      ],
      placeholder: 'Filter by visibility',
    },
  ];

  // Form sections configuration
  const formSections: FormSection[] = [
    {
      title: 'Incoterm Information',
      description: 'Enter the incoterm details.',
      fields: [
        {
          name: 'srNo',
          label: 'SR No.',
          type: 'text',
          required: true,
          placeholder: 'Enter serial number (e.g., "1", "2", "3")',
          description: 'Serial number for ordering (1, 2, 3, etc.)',
          validation: z
            .string()
            .min(1, 'Serial number is required')
            .max(20, 'Serial number cannot exceed 20 characters'),
        },
        {
          name: 'name',
          label: 'Incoterm Name',
          type: 'text',
          required: true,
          placeholder: 'Enter incoterm name (e.g., "Free On Board", "Cost Insurance Freight")',
          description: 'A unique name for this incoterm',
          validation: z
            .string()
            .min(2, 'Name must be at least 2 characters')
            .max(200, 'Name cannot exceed 200 characters'),
        },
        {
          name: 'code',
          label: 'Incoterm Code',
          type: 'text',
          required: true,
          placeholder: 'Enter incoterm code (e.g., "FOB", "CIF", "EXW")',
          description: 'A unique code for this incoterm',
          validation: z
            .string()
            .min(2, 'Code must be at least 2 characters')
            .max(20, 'Code cannot exceed 20 characters'),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Current status of this incoterm',
        },
        {
          name: 'hideInPortal',
          label: 'Hide in Portal',
          type: 'radio',
          required: false,
          options: [
            { value: 'false', label: 'Visible - Show in customer portal' },
            { value: 'true', label: 'Hidden - Hide from customer portal' },
          ],
          description: 'Select if you want to hide this incoterm from the customer portal',
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingIncoterm(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (incoterm: Incoterm) => {
    setEditingIncoterm({
      ...incoterm,
      hideInPortal: incoterm.hideInPortal ?? false,
    });
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (incoterm: Incoterm) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete "${incoterm.name}"? This action cannot be undone.`
      )
    ) {
      deleteIncotermMutation.mutate(incoterm._id);
    }
  };

  const handleSubmit = async (data: any) => {
    // Transform the data
    const transformedData = {
      ...data,
      code: data.code.toUpperCase(),
      hideInPortal: data.hideInPortal === 'true' || data.hideInPortal === true,
    };

    if (editingIncoterm) {
      updateIncotermMutation.mutate({
        id: editingIncoterm._id,
        data: transformedData,
      });
    } else {
      createIncotermMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingIncoterm(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'Incoterm data has been refreshed',
      variant: 'default',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/incoterm/export?${params.toString()}`
      );

      if (response.data.success) {
        const incoterms = response.data.data;

        const exportData = incoterms.map((inc: Incoterm) => ({
          'SR No.': inc.srNo,
          Name: inc.name,
          Code: inc.code,
          Status: inc.status,
          'Hide in Portal': inc.hideInPortal ? 'Yes' : 'No',
          'Created At': new Date(inc.createdAt).toLocaleString(),
          'Updated At': new Date(inc.updatedAt).toLocaleString(),
        }));

        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map((row: any) =>
            headers
              .map((header) => {
                const value = row[header];
                if (
                  typeof value === 'string' &&
                  (value.includes(',') || value.includes('"'))
                ) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              })
              .join(',')
          ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `incoterms-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Success',
          description: `Exported ${incoterms.length} incoterms`,
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description:
          error.response?.data?.error?.message || 'Failed to export incoterms',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            Incoterm Management
          </h1>
          <p className='text-muted-foreground'>
            Manage international commercial terms for trade
          </p>
        </div>
      </div>

      <MasterDataTable<Incoterm>
        title='Incoterms'
        description='Manage and configure incoterm information'
        data={incoterms}
        columns={columns}
        isLoading={isLoading}
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search incoterms by name, code, or SR no...'
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingIncoterm ? 'Edit Incoterm' : 'Create Incoterm'}
        sections={formSections}
        initialData={editingIncoterm ? {
          ...editingIncoterm,
          hideInPortal: String(editingIncoterm.hideInPortal ?? false),
        } : undefined}
        onSubmit={handleSubmit}
        isLoading={
          createIncotermMutation.isPending || updateIncotermMutation.isPending
        }
        mode={editingIncoterm ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default IncotermManagement;


